import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import FoodViewer from './FoodViewer';
import Cart from './Cart';

// ---------------------------------------------------------
// 🔧 CONFIGURATION: YOUR REAL ID IS HERE NOW
// ---------------------------------------------------------
const DEMO_RESTAURANT_ID = '916589f4-1217-4011-8a05-8583b850886b'; 
// ---------------------------------------------------------

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(price);
};

function SavoriaDemo() {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState(["All", "Starters", "Main Course", "Desserts", "Drinks"]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);

  // FETCH DATA FROM SUPABASE (Filtered by Demo ID)
  useEffect(() => {
    fetchDemoItems();
  }, []);

  const fetchDemoItems = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('restaurant_id', DEMO_RESTAURANT_ID) // <--- USES YOUR ID
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        setMenuItems(data);
        // Dynamically extract categories from your uploaded items
        const uniqueCats = ["All", ...new Set(data.map(item => item.category))];
        setCategories(uniqueCats);
      }
    } catch (err) {
      console.error("Error fetching demo menu:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = activeCategory === "All" ? menuItems : menuItems.filter(item => item.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#FDF8F6] font-sans text-gray-900 flex flex-col">
      
      {/* --- HEADER --- */}
      <header className="bg-white py-4 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-theme-orange rounded-lg flex items-center justify-center text-white text-xl shadow-md">
              🍽️
            </div>
            <div className="leading-tight">
              <h1 className="text-xl font-bold text-gray-900">Savoria <span className="text-xs text-theme-orange border border-theme-orange px-1 rounded ml-1">DEMO</span></h1>
              <p className="text-[10px] text-gray-400 font-medium tracking-wide uppercase">Interactive Menu Preview</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => setShowCart(true)} className="relative group">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700 group-hover:text-theme-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              {cartItems.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-white">
                  {cartItems.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 md:py-20 relative overflow-hidden bg-white">
        <div className="container mx-auto px-6 flex flex-col items-center text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full border border-orange-100 mb-6">
              <span className="text-theme-orange text-lg">👀</span>
              <span className="text-sm font-bold text-gray-600 tracking-wide">Live Preview Mode</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-extrabold leading-[1.1] text-gray-900 mb-6">
              This is how your customers <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-theme-orange to-red-500">will see your menu</span>
            </h2>
            <p className="text-lg text-gray-500 max-w-lg leading-relaxed mb-8">
              Everything you see below is managed directly from the Dashboard.
            </p>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-12 flex-1">
        <div className="container mx-auto px-6">
          
          {/* Categories */}
          <div className="flex justify-center gap-3 mb-12 overflow-x-auto pb-4 scrollbar-hide">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2.5 rounded-full font-bold transition-all whitespace-nowrap ${
                  activeCategory === category
                    ? 'bg-theme-orange text-white shadow-lg shadow-orange-500/25'
                    : 'bg-white text-gray-500 hover:text-theme-orange hover:bg-orange-50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Loading / Empty States */}
          {loading && (
            <div className="text-center py-20 text-gray-400">Loading demo items...</div>
          )}
          
          {!loading && menuItems.length === 0 && (
             <div className="text-center py-20">
               <div className="text-5xl mb-4">✨</div>
               <h3 className="text-xl font-bold text-gray-700">Demo is empty</h3>
               <p className="text-gray-500 mt-2">Add items to your Demo Restaurant in the Dashboard.</p>
             </div>
          )}

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map(item => (
              <div key={item.id} className="bg-white rounded-3xl p-4 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 group">
                <div className="relative h-64 rounded-2xl overflow-hidden bg-gray-50 mb-4">
                  <FoodViewer 
                    modelSrc={item.model_url} 
                    imageSrc={item.image_url} 
                    itemName={item.name} 
                  />
                  <div className="absolute top-3 left-3 flex gap-2 pointer-events-none">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-md text-gray-900 text-xs font-bold rounded-lg shadow-sm flex items-center gap-1">
                      {item.category}
                    </span>
                  </div>
                </div>
                <div className="px-2 pb-2">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-xl font-bold text-gray-900">{item.name}</h4>
                    <span className="text-xl font-bold text-theme-orange">{formatPrice(item.price)}</span>
                  </div>
                  <p className="text-gray-400 text-sm mb-6 line-clamp-2 min-h-[40px]">{item.description}</p>
                  <button
                    onClick={() => setCartItems([...cartItems, item])}
                    className="w-full py-3.5 bg-theme-orange text-white rounded-xl font-bold hover:bg-orange-600 active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    <span>+ Add to Order</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- RICH FOOTER --- */}
      <footer className="bg-gray-900 text-white pt-12 pb-8 border-t border-gray-800">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            
            {/* Restaurant Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-theme-orange rounded-md flex items-center justify-center text-white text-lg">
                  🍽️
                </div>
                <h3 className="text-xl font-bold">Savoria</h3>
              </div>
              <p className="text-gray-400 text-sm">
                Thank you for dining with us. We hope you enjoy your meal as much as we enjoyed creating it for you.
              </p>
            </div>

            {/* Quick Info */}
            <div>
              <h4 className="font-bold text-theme-orange mb-4">Visit Us</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <span>📍</span> Ask staff for Wi-Fi
                </li>
                <li className="flex items-center gap-2">
                  <span>⏰</span> Open Daily: 11 AM - 11 PM
                </li>
              </ul>
            </div>

            {/* Socials */}
            <div>
              <h4 className="font-bold text-theme-orange mb-4">Follow Us</h4>
              <div className="flex gap-4">
                 <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-theme-orange transition-colors cursor-pointer">📷</div>
                 <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-theme-orange transition-colors cursor-pointer">👍</div>
              </div>
            </div>

          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-xs text-gray-500">
            <p className="mb-2">© 2024 Savoria Restaurant. All rights reserved.</p>
            <p>
              Digital Menu Powered by <a href="https://tablear.in" target="_blank" rel="noreferrer" className="text-theme-orange font-bold hover:underline">TableAR</a>
            </p>
          </div>
        </div>
      </footer>

      {/* Cart Modal */}
      {showCart && <Cart cartItems={cartItems} onClose={() => setShowCart(false)} onRemove={(i) => {
         const newCart = [...cartItems];
         newCart.splice(i, 1);
         setCartItems(newCart);
      }} />}
    </div>
  );
}

export default SavoriaDemo;